/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
import React, { useEffect, useMemo, useState } from 'react';

import { Box, Stack, xcss } from '@atlaskit/primitives';
import {
	AgentAvatar,
	AgentBanner,
	AgentProfileInfo,
	AgentStarCount,
	type ConversationStarter,
} from '@atlaskit/rovo-agent-components';
import Spinner from '@atlaskit/spinner';
import {
	AgentInstructions,
	useAgentDetailsController,
} from '@atlassian/conversation-assistant-agent';
import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';
import {
	type HttpError,
	isAuthError,
	useDialog,
	useExpTrackerStart,
	useExpTrackerStop,
	usePrincipalUser,
	UserExperienceNames,
	useSite,
} from '@atlassian/ptc-common';

import { trimUserARI } from '../../hooks/utils';
import { useRovoAgentData } from '../../state/RovoAgent';
import { App as AppWrapper } from '../App/App.styled';
import ContainersInProfilePage from '../CGContainers';
import DocumentTitle from '../DocumentTitle';
import { AsideColumn, Body, MainColumn, ResponsiveSection } from '../PageLayout';
import { ErrorScreen } from '../ProfilePage/ErrorScreen';
import ProfileWorkList from '../ProfileWorkList';

import { AgentAboutPanel } from './AgentAboutPanel';
import { AgentActions } from './AgentActions';
import { AgentConversationStartersSection } from './AgentConversationStarters';
import { AgentCreator } from './AgentCreator';
import AgentDeleteConfirmationModal from './AgentDeleteConfirmationModal';
import { type AgenttProfilePageProps } from './types';
import { useAgentActions } from './useAgentActions';

const AGENT_PROFILE_PAGE_LATENCY_TARGET = 1000;

const mainColumnStyle = xcss({});
const bannerStyles = xcss({
	marginBottom: 'space.500',
	marginInline: 'space.negative.400',
});

const avatarStyles = xcss({
	marginBottom: 'space.300',
});

export const AgentProfilePageComponent = (props: AgenttProfilePageProps) => {
	const { agentId } = props;
	const [{ cloudId }] = useSite();
	const [{ agentData }, { setAgentInfo }] = useRovoAgentData();
	const [{ principalUserId }] = usePrincipalUser();
	const { fireEvent } = usePeopleTeamAnalyticsEvents();

	const [starCount, setStarCount] = useState(0);
	const [isStarred, setIsStarred] = useState(false);

	const assistanceServiceParams = useMemo(() => {
		return {
			experienceId: 'profile-page',
			product: 'rovo',
			siteId: cloudId,
			touchpointSource: 'agent-profile-page',
		};
	}, [cloudId]);

	const { data, error, isFetching } = useAgentDetailsController({
		agentId,
		enabled: agentData?.id !== agentId,
		assistanceServiceParams,
	});

	useEffect(() => {
		fireEvent('screen.agentProfile.viewed', {});
	}, [fireEvent]);

	useExpTrackerStart({
		id: agentId,
		name: UserExperienceNames.AGENT_PROFILE_PAGE,
		latencySloMs: AGENT_PROFILE_PAGE_LATENCY_TARGET,
	});

	useExpTrackerStop({
		name: UserExperienceNames.AGENT_PROFILE_PAGE,
		checkShouldAbort: (error) => {
			if (!isAuthError(error as HttpError)) {
				return;
			}

			return error.message;
		},
		error: error as unknown as Error,
	});

	const agentIdentityId = useMemo(() => {
		return agentData?.identity_account_id ? trimUserARI(agentData?.identity_account_id) : '';
	}, [agentData?.identity_account_id]);

	const isAgentCreatedByCurrentUser = useMemo(() => {
		return agentData?.creator_type === 'CUSTOMER'
			? trimUserARI(agentData?.creator || '') === principalUserId
			: false;
	}, [agentData?.creator, agentData?.creator_type, principalUserId]);

	const { openDialog } = useDialog();

	useEffect(() => {
		if (data) {
			setAgentInfo(data);
			setStarCount(data.favourite_count);
			setIsStarred(data.favourite);
		}
	}, [data, setAgentInfo]);

	const {
		setFavouriteAgent,
		editAgent,
		copyAgent,
		duplicateAgent,
		deleteAgent,
		onChatWithAgent,
		startConversation,
	} = useAgentActions({
		agentId: agentData?.id,
		agentIdentityAccountId: agentData?.identity_account_id,
		agentName: agentData?.name,
		assistanceServiceParams,
	});

	const handleOnDeleteAgent = () => {
		openDialog(AgentDeleteConfirmationModal, {
			agentName: agentData?.name,
			agentId: agentData?.id,
			onDelete: deleteAgent,
			onClose: () => {},
		});
	};

	const isForgeAgent = useMemo(
		() => agentData?.creator_type === 'THIRD_PARTY' || agentData?.creator_type === 'FORGE',
		[agentData?.creator_type],
	);
	const handleCopyInstructions = () => {
		if (agentData?.system_prompt_template) {
			navigator.clipboard.writeText(agentData?.system_prompt_template.toString());
		}
	};

	if (error) {
		return <ErrorScreen error={error} key={agentId} />;
	}

	if (isFetching) {
		<AppWrapper>
			<Spinner size="medium" />
		</AppWrapper>;
	}

	if (!agentData) {
		return null;
	}

	const handleFavouriteAgent = async () => {
		const onSuccessCallBack = () => {
			setStarCount(isStarred ? starCount - 1 : starCount + 1);
			setIsStarred(!isStarred);
		};
		setFavouriteAgent(!isStarred, onSuccessCallBack);
	};

	return (
		<>
			<DocumentTitle title={agentData.name}>
				<AppWrapper>
					<Box xcss={bannerStyles}>
						<AgentBanner
							agentId={agentData.id}
							agentNamedId={agentData.external_config_reference ?? agentData.named_id}
							agentIdentityAccountId={agentData.identity_account_id}
							height={192}
						/>
					</Box>
					<Body>
						<ResponsiveSection>
							<AsideColumn>
								<Stack space="space.300">
									<Stack space="space.200">
										<Box xcss={avatarStyles} style={{ marginTop: '-105px' }}>
											<AgentAvatar
												agentId={agentData.id}
												agentNamedId={agentData.external_config_reference ?? agentData.named_id}
												agentIdentityAccountId={agentData.identity_account_id}
												size="xxlarge"
												isForgeAgent={
													agentData.creator_type === 'FORGE' ||
													agentData.creator_type === 'THIRD_PARTY'
												}
												forgeAgentIconUrl={agentData.icon}
											/>
										</Box>
										<AgentProfileInfo
											agentName={agentData?.name}
											isStarred={isStarred}
											onStarToggle={() => handleFavouriteAgent()}
											creatorRender={
												<AgentCreator
													create_type={agentData.creator_type}
													creator={agentData.creator || undefined}
												/>
											}
											starCountRender={
												<AgentStarCount isLoading={isFetching} starCount={starCount} />
											}
											agentDescription={agentData.description}
											isHidden={agentData.visibility === 'PRIVATE'}
										/>

										<AgentActions
											agentId={agentData.id}
											isAgentCreatedByUser={isAgentCreatedByCurrentUser}
											onEditAgent={editAgent}
											onCopyAgent={copyAgent}
											onDuplicateAgent={duplicateAgent}
											onDeleteAgent={handleOnDeleteAgent}
											onChatClick={onChatWithAgent}
											isForgeAgent={isForgeAgent}
											assistanceServiceParams={assistanceServiceParams}
										/>
									</Stack>
									<AgentAboutPanel
										agent={agentData}
										assistanceServiceParams={assistanceServiceParams}
									/>
								</Stack>
							</AsideColumn>
							<MainColumn>
								<Stack space="space.500" xcss={mainColumnStyle}>
									<AgentConversationStartersSection
										isAgentDefault={agentData.is_default}
										userDefinedConversationStarters={agentData.user_defined_conversation_starters?.map(
											(starter) => {
												return {
													message: starter,
													type: 'user-defined',
												};
											},
										)}
										onConversationStarterClick={(conversationStarter: ConversationStarter) => {
											startConversation(conversationStarter.message);
										}}
									/>

									<AgentInstructions
										promptTemplate={agentData.system_prompt_template}
										onCopy={handleCopyInstructions}
										isForgeAgent={isForgeAgent}
										fullHeightText={false}
									/>

									<ProfileWorkList userId={agentIdentityId} />
									<ContainersInProfilePage
										userId={agentIdentityId}
										status={agentData.deactivated ? 'inactive' : 'active'}
										userFullName={agentData.name}
									/>
								</Stack>
							</MainColumn>
						</ResponsiveSection>
					</Body>
				</AppWrapper>
			</DocumentTitle>
		</>
	);
};
